import dayjs from "dayjs";

import * as yup from "core/hookForm/yup";
import i18n from "core/i18n";
import { FundsTransferType, SignatureType, SubjectTypeEnum, VehicleCancellationReason } from "types/enums";
import { ChangeSubject, ServiceHubBankAccount } from "types/common";

import { EDITABLE_DATE_REASONS_CZ, EDITABLE_DATE_REASONS_SK } from "./constants";
import { FormStep, VehicleCancellationFormValues } from "./types";

type Step0FormValues = Pick<VehicleCancellationFormValues, "identificationNumberVerify">;

type Step1FormValues = Pick<
    VehicleCancellationFormValues,
    | "changeSheetId"
    | "cancellationReason"
    | "cancellationDate"
    | "cancellationNote"
    | "vehicleRemovedFromRegistrationStatement"
    | "contactPerson"
    | "fundsTransfer"
    | "fundsTransferBankAccount"
>;

type Step2SubmitCancellationFormValues = Pick<VehicleCancellationFormValues, "signatureType" | "dateOfSigning" | "placeOfSigning">;

const whenSkSite = (schema: yup.StringSchema) =>
    yup.string().when("$isSkSite", {
        is: true,
        then: () => schema,
        otherwise: () => yup.string().strip(),
    });

const whenCzSite = (schema: yup.StringSchema) =>
    yup.string().when("$isSkSite", {
        is: false,
        then: () => schema,
        otherwise: () => yup.string().strip(),
    });

export default function () {
    const { t } = i18n.useTranslation();

    const emptySchema: yup.ObjectSchema<{}> = yup.object().shape({});

    const step0IntroductionSchema: yup.ObjectSchema<Step0FormValues> = yup.object({
        identificationNumberVerify: yup.string().when("$isAuthenticated", {
            is: false,
            then: (schema) => schema.required(t("error.required")),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const addressSchema = yup.object().shape({
        street: yup.string().required(t("error.required")),
        descriptionNumber: yup.string().required(t("error.required")),
        orientationNumber: yup.string().notRequired(),
        city: yup.string().required(t("error.required")),
        postalCode: yup.string().required(t("error.required")),
        countryCode: yup.string().required(t("error.required")),
    });

    const personSchema = yup.object().shape({
        birthNumber: yup.string().required(t("error.required")).validBirthNumber(t),
        firstName: yup.string().required(t("error.required")).min(2, t("error.minTwoLettersRequired")),
        lastName: yup.string().required(t("error.required")).min(2, t("error.minTwoLettersRequired")),
        phoneNumber: yup.string().required(t("error.required")),
        email: yup.string().email(t("error.email.notValid")).required(t("error.required")),
        address: addressSchema,
    });

    const organisationSchema = yup.object().shape({
        companyIdentificationNumber: yup.string().required(t("error.required")),
        // .test("organisationId", t("error.notValid"), async function _(value) {
        //   return await debouncedValidateOrganisationId(value);
        // }),
        companyName: yup.string().required(t("error.required")),
    });

    const subjectSchema = yup.lazy((value) => {
        // Avoid errors when Yup is building schema shape.
        if (!value || typeof value !== "object") {
            return yup.object();
        }

        if (value.subjectType === SubjectTypeEnum.COMPANY) {
            return organisationSchema;
        } else {
            return personSchema;
        }
    });

    const bankAccountSchema = yup.object({
        prefix: whenCzSite(yup.string().validAccountPrefix(t).nullable()),
        accountNumber: whenCzSite(yup.string().required(t("error.required")).validAccountNumber(t)),
        bankCode: whenCzSite(yup.string().required(t("error.required"))),
        iban: whenSkSite(yup.string().required(t("error.required")).validIban(t)),
    });

    const step1CancellationParamsSchema: yup.ObjectSchema<Step1FormValues> = yup.object({
        changeSheetId: yup.string().required(t("error.required")),

        cancellationReason: yup
            .mixed<VehicleCancellationReason>()
            .oneOf(Object.values(VehicleCancellationReason))
            .required(t("error.required")),

        cancellationDate: yup
            .string()
            .required(t("error.required"))
            .when(["cancellationReason", "$isSkSite"], ([cancellationReason, isSkSite], schema: yup.StringSchema) => {
                const reasonsWithEditableDate = isSkSite ? EDITABLE_DATE_REASONS_SK : EDITABLE_DATE_REASONS_CZ;

                if (reasonsWithEditableDate.includes(cancellationReason)) {
                    const today = dayjs();
                    if (cancellationReason === VehicleCancellationReason.BY_AGREEMENT) {
                        return schema.isDateSameOrAfter(t, today.add(5, "day"));
                    }
                    return schema.isDateSameOrBefore(t, today);
                }
                return schema;
            }),

        cancellationNote: yup.string().when("cancellationReason", {
            is: VehicleCancellationReason.BY_AGREEMENT,
            then: () => yup.string().required(t("error.required")),
            otherwise: () => yup.string().nullable(),
        }),

        vehicleRemovedFromRegistrationStatement: yup.boolean().when("cancellationReason", {
            is: VehicleCancellationReason.REMOVED_FROM_REGISTER,
            then: () => yup.boolean().oneOf([true], t("error.required")).required(t("error.required")),
            otherwise: () => yup.boolean().nullable(),
        }),

        contactPerson: yup.mixed<ChangeSubject>().when("cancellationReason", {
            is: VehicleCancellationReason.INSURER_DEATH,
            then: () => subjectSchema,
            otherwise: () => yup.mixed<ChangeSubject>().strip(),
        }),

        fundsTransfer: yup.mixed<FundsTransferType>().oneOf(Object.values(FundsTransferType)).required(t("error.required")),

        fundsTransferBankAccount: yup.mixed<ServiceHubBankAccount>().when("fundsTransfer", {
            is: FundsTransferType.OTHER_BANK_ACCOUNT,
            then: () => bankAccountSchema,
            otherwise: () => yup.mixed<ServiceHubBankAccount>().strip(),
        }),
    });

    const step2SubmitCancellationSchema: yup.ObjectSchema<Step2SubmitCancellationFormValues> = yup.object({
        signatureType: yup.mixed<SignatureType>().oneOf(Object.values(SignatureType)).required(t("error.required")),

        dateOfSigning: yup.string().when("signatureType", {
            is: SignatureType.PHYSICAL,
            then: () => yup.string().required(t("error.required")),
            otherwise: () => yup.string().nullable(),
        }),

        placeOfSigning: yup.string().when("signatureType", {
            is: SignatureType.PHYSICAL,
            then: () => yup.string().required(t("error.required")),
            otherwise: () => yup.string().nullable(),
        }),
    });

    const schemas: Record<FormStep, yup.ObjectSchema<any>> = {
        [FormStep.NOT_FEASIBLE]: emptySchema,
        [FormStep.S0_INTRODUCTION]: step0IntroductionSchema,
        [FormStep.S1_CANCELLATION_PARAMS]: step1CancellationParamsSchema,
        [FormStep.S2_REVIEW_SUBMIT]: step2SubmitCancellationSchema,
    };

    return schemas;
}
